import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from './shared/shared.module';
import { StoreModule } from '@ngrx/store';
import { APP_VERSION, environment } from 'src/environments/environment';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { AuthEffects } from './features/auth/store/auth.effects';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CatalogsEffects } from './features/catalogs/store/catalogs.effects';
import { BrowserModule } from '@angular/platform-browser';
import { AuthModule } from './features/auth/auth.module';
import { LayoutModule } from './layout/layout.module';
import { CatalogsModule } from './features/catalogs/catalogs.module';
import { WishlistModule } from './features/wishlist/wishlist.module';
import { EffectsModule } from '@ngrx/effects';
import { WishlistsEffects } from './features/wishlist/store/wishlist.effects';
import { TokenInterceptor } from './core/interceptors/token.interceptor';
import { LocalStorageService } from './shared/localstorage.service';
import { SharedEffects } from './shared/store/shared.effects';
import { QuotesModule } from './features/quotes/quotes.module';
import { QuotesEffects } from './features/quotes/store/quotes.effects';
import { OrdersModule } from './features/orders/orders.module';
import { ordersEffects } from './features/orders/store/orders.effects';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MatMomentDateModule,
} from '@angular/material-moment-adapter';
import { reducers } from './core/app.state';
import { HttpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { DateLocaleService } from './shared/services/date-locale.service';
import { MAT_DATE_LOCALE } from '@angular/material/core';

export function httpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    StoreModule.forRoot(reducers),
    EffectsModule.forRoot([
      AuthEffects,
      CatalogsEffects,
      WishlistsEffects,
      SharedEffects,
      QuotesEffects,
      ordersEffects,
    ]),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    MatMomentDateModule,
    SharedModule,
    AuthModule,
    LayoutModule,
    CatalogsModule,
    OrdersModule,
    WishlistModule,
    QuotesModule,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (dateLocaleService: DateLocaleService) => dateLocaleService.getCurrentLocale(),
      deps: [DateLocaleService]
    },
    LocalStorageService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor() {
    this.handleNewVersion();
  }

  handleNewVersion() {
    const currentVersion = localStorage.getItem('app_version'),
      isNewVersion = currentVersion !== APP_VERSION;

    if (isNewVersion) {
      localStorage.setItem('app_version', APP_VERSION);
      location.reload();
    }
    console.log(`App Version: ${APP_VERSION}`);
  }
}
